// Shared constants and message shapes used by both the server and the client.
// Units follow Bombermaaan: 32px blocks, positions are the top-left pixel of an
// element's 32x32 box, so an element standing on block (bx, by) is at (bx*32, by*32).

export const COLS = 15;
export const ROWS = 13;
export const BLOCK = 32;
export const TICK_RATE = 30; // snapshots per second (the simulation runs at 60 Hz)
export const MAX_PLAYERS = 4;
export const WINS_TO_MATCH = 3;

export const enum Tile {
  Empty = 0,
  Hard = 1,
  Soft = 2,
}

export const enum Dir {
  None = 0,
  Up = 1,
  Down = 2,
  Left = 3,
  Right = 4,
}

// Order matches the pairs of sprites in arena_item.png.
export const enum Item {
  Bomb = 0,
  Flame = 1,
  Kick = 2,
  Roller = 3,
  Skull = 4,
  Throw = 5,
  Punch = 6,
  Remote = 7,
  Shield = 8,
  StrongWeak = 9,
}
export const ITEM_KINDS = 10;

export const enum Sickness {
  None = -1,
  Inversion = 0,
  SmallFlame = 1,
  Constipated = 2,
  Colic = 3,
  Slow = 4,
  Fast = 5,
  Inertia = 6,
  LongBomb = 7,
  ShortBomb = 8,
  Invisibility = 9,
  Flameproof = 10,
}
export const SICKNESSES = 11;

export const enum BState {
  Walk = 0,
  Lift = 1,
  Hold = 2,
  Throw = 3,
  Punch = 4,
  Stunned = 5,
  Dying = 6,
  Dead = 7,
}

export type Phase = "lobby" | "countdown" | "playing" | "roundover" | "matchover";

export interface PlayerView {
  id: string;
  name: string;
  color: number; // 0..3 (white, black, red, blue)
  x: number; // pixels
  y: number;
  dir: Dir; // facing
  moving: boolean;
  state: BState;
  st: number; // seconds spent in the current state
  alive: boolean; // false once dying starts
  playing: boolean; // false for someone waiting for the next round
  wins: number;
  bombs: number;
  fire: number;
  speed: number; // px per second
  kick: boolean;
  throw: boolean;
  punch: boolean;
  remote: boolean;
  shield: number; // seconds of shield left
  sick: Sickness;
  connected: boolean;
  bot: boolean;
}

export interface BombView {
  id: number;
  x: number; // pixels
  y: number;
  z: number; // height above the floor while held or flying
  remote: boolean;
  fuse: number; // seconds left (remote bombs that are not triggered report 99)
  air: boolean; // held or flying: drawn above the bombers
}

export interface ExplosionView {
  x: number; // block
  y: number;
  t: number; // seconds since it started
  r: [number, number, number, number]; // flame cells drawn up, down, left, right
  e: number; // bitmask (up 1, down 2, left 4, right 8): the arm reached full length, draw a tip
}

export interface ItemView {
  x: number; // pixels
  y: number;
  k: Item;
  burn?: number; // seconds since it caught fire
  fly?: boolean;
  fumes?: number; // seconds since it reappeared in a puff of smoke
}

export type GameEvent =
  | "drop"
  | "boom1"
  | "boom2"
  | "boom3"
  | "boom4"
  | "boom5"
  | "boom6"
  | "boom7"
  | "boom8"
  | "boom9"
  | "boom10"
  | "bounce"
  | "throw"
  | "punch"
  | "pick"
  | "sick"
  | "contaminate"
  | "lose"
  | "death"
  | "fumes"
  | "hurry"
  | "clap"
  | "ding"
  | "winner"
  | "draw"
  | "victory"
  | "round";

export interface Snapshot {
  t: "s";
  tick: number;
  code: string;
  phase: Phase;
  hostId: string;
  level: string; // name of the level being played, or chosen in the lobby
  levelChoice: number; // -1 random, else index into the level list
  levels: string[];
  grid: string; // ROWS*COLS digits (Tile), row-major
  arrows: [number, Dir][]; // floor arrows: cell index, direction
  burning: [number, number][]; // soft walls on fire: cell index, seconds burning
  falling: [number, number][]; // walls dropping from the sky: cell index, seconds falling
  players: PlayerView[];
  bombs: BombView[];
  explosions: ExplosionView[];
  items: ItemView[];
  timer: number; // seconds left in the current phase (countdown / round clock)
  hurry: number; // seconds since the HURRY banner appeared, -1 before
  message: string;
  winnerId: string | null;
  round: number;
  fast: boolean; // the round is running sped up
  canFast: boolean; // only computer players are left in the round, so it may be sped up
  events: GameEvent[];
}

export type ClientMsg =
  | { t: "create"; name: string }
  | { t: "join"; code: string; name: string; token?: string }
  | { t: "start" }
  | { t: "addBot" }
  | { t: "removeBot"; id: string }
  | { t: "level"; level: number }
  | { t: "input"; dir: Dir; a: boolean } // a: action button 1 held (drop / lift and throw)
  | { t: "b" } // action button 2 pressed (stop kicked bombs / punch / remote)
  | { t: "fast"; on: boolean } // speed up a round that only computer players are left in
  | { t: "chat"; text: string }
  | { t: "leave" };

export type ServerMsg =
  | { t: "joined"; code: string; you: string; token: string }
  | { t: "error"; message: string }
  | { t: "chat"; messages: ChatMessage[] }
  | Snapshot;

export const CHAT_MAX_LENGTH = 200;

export interface ChatMessage {
  id: number;
  at: number; // ms since epoch
  from: string; // display name, empty for system notices
  color: number; // seat color, -1 for system notices
  text: string;
}

export const CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export const CODE_LENGTH = 5;

export function normalizeCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, CODE_LENGTH);
}
